import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { SiteCreditBalance } from 'src/app/models/siteCreditBalance.model';
import { AuthService } from 'src/app/services';
import { CommonService } from 'src/app/services/common.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { SiteCreditBalanceService } from 'src/app/services/siteCreditBalance.service';
import { ToastService } from 'src/app/services/toast.service';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { getAiAnalystAdminRouteConfig } from 'src/app/shared/constants/aiAnalystAdminRouteConfig.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { CreditRunway, describeCreditRunway } from './credit-runway.util';

@Component({
  standalone: false,
  selector: 'app-site-credit-balance-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './site-credit-balance-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DateFormatPipe, DecimalPipe]
})
export class SiteCreditBalanceListComponent extends CoreGridComponent<SiteCreditBalance> implements OnInit, AfterViewInit {

  constructor(
    private siteCreditBalanceService: SiteCreditBalanceService,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private dateFormatPipe: DateFormatPipe,
    private dateFormatService: DateFormatService,
    private decimalPipe: DecimalPipe,
    private translateService: TranslateService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = siteCreditBalanceService;
    this.canRead = true;
    this.addDisabled = true;
    this.editDisabled = true;
    this.deleteDisabled = true;
  }

  protected get objectId(): string {
    return RolePermissionObjects.SiteCreditBalance;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  protected override get showActionsColumn(): boolean {
    return false;
  }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<SiteCreditBalanceListComponent>, null);
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Balance';
    this.searchPlaceholder = 'Search Balances';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.tenantName),
        displayName: 'Org',
        cellTemplate: (e) => escapeHtml(e.tenantName) || '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.organizationLevelId),
        displayName: 'Paying level',
        cellTemplate: (e) => escapeHtml(e.organizationLevelName) || '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.balance),
        displayName: 'Balance',
        cellTemplate: (e) => this.decimalPipe.transform(e.balance, '1.2-2') ?? '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.usedThisMonth),
        displayName: 'Used this month',
        cellTemplate: (e) => this.decimalPipe.transform(e.usedThisMonth, '1.2-2') ?? '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.creditsPerDay),
        displayName: 'Per day',
        cellTemplate: (e) => e.creditsPerDay === null ? '-' : this.decimalPipe.transform(e.creditsPerDay, '1.1-1') ?? '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.daysRemaining),
        displayName: 'Lasts about',
        cellTemplate: (e) => this.runwayCell(e)
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.lastLoadedDate),
        displayName: 'Last loaded',
        cellTemplate: (e) => this.lastLoadedCell(e)
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.dateCreated),
        displayName: 'Created',
        cellTemplate: (e) => e.dateCreated ? this.dateFormatPipe.transform(e.dateCreated, this.dateFormatService.userDateWithTimeFormat) : '-'
      }),
      new ColumnConfig<SiteCreditBalance>({
        name: nameOf<SiteCreditBalance>(x => x.dateUpdated),
        displayName: 'Updated',
        cellTemplate: (e) => e.dateUpdated ? this.dateFormatPipe.transform(e.dateUpdated, this.dateFormatService.userDateWithTimeFormat) : '-'
      }),
      { name: 'action', displayName: 'Actions' }
    ];
  }

  private runwayCell(balance: SiteCreditBalance): string {
    const runway = describeCreditRunway(balance.daysRemaining, new Date());
    const label = this.runwayLabel(runway);
    return `<span class="credit-runway-pill credit-runway-${runway.tone}">${escapeHtml(label)}</span>`;
  }

  private runwayLabel(runway: CreditRunway): string {
    switch (runway.kind) {
      case 'noUse':
        return this.translateService.instant('no use yet');
      case 'overYear':
        return this.translateService.instant('over a year');
      default:
        return this.translateService.instant(runway.days === 1 ? '{{days}} day · {{date}}' : '{{days}} days · {{date}}', {
          days: runway.days,
          date: this.dateFormatPipe.transform(runway.runsOutOn, this.dateFormatService.userDateFormat)
        });
    }
  }

  private lastLoadedCell(balance: SiteCreditBalance): string {
    if (!balance.lastLoadedDate) {
      return '-';
    }
    const date = this.dateFormatPipe.transform(balance.lastLoadedDate, this.dateFormatService.userDateFormat);
    const amount = this.decimalPipe.transform(balance.lastLoadedAmount ?? 0, '1.0-2');
    return `${escapeHtml(date)} · +${escapeHtml(amount ?? '')}`;
  }

  override initializeTabs(): { routerLink: string; title: string; objectId?: string }[] {
    const currentUser = this.authService.getCurrentUser();
    return getAiAnalystAdminRouteConfig(currentUser.userType === SecurityProfileEnum.SuperAdmin, !!currentUser.isAiAnalystEnabled);
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(_entityId?: string): void {
    return;
  }

  override openEntityDeleteDialog(_entity: SiteCreditBalance): void {
    return;
  }
}
